import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

// ── Constants ─────────────────────────────────────────────────────────────────
const COLUMNS = [
  { id:'backlog',     label:'Backlog',     color:'#94A3B8', bg:'#F8FAFC' },
  { id:'todo',        label:'To Do',       color:'#073E81', bg:'#EFF6FF' },
  { id:'in_progress', label:'In Progress', color:'#D97706', bg:'#FFFBEB' },
  { id:'review',      label:'In Review',   color:'#7C3AED', bg:'#F5F3FF' },
  { id:'done',        label:'Done',        color:'#059669', bg:'#ECFDF5' },
];

const PRIORITY_META = {
  none:   { label:'No priority', color:'#94A3B8', dot:'#CBD5E1' },
  low:    { label:'Low',         color:'#64748B', dot:'#94A3B8' },
  medium: { label:'Medium',      color:'#D97706', dot:'#FCD34D' },
  high:   { label:'High',        color:'#EA580C', dot:'#FB923C' },
  urgent: { label:'Urgent',      color:'#DC2626', dot:'#F87171' },
};

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-IN', { day:'numeric', month:'short' });
}
function daysDue(d) {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / 86400000);
}
function initials(name='') {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

// ── New Task Modal ────────────────────────────────────────────────────────────
function NewTaskModal({ projectId, defaultStatus, users, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', description: '', status: defaultStatus || 'todo',
    priority: 'medium', assigned_to: '', due_date: '', estimated_hours: '', tags: '',
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    if (!form.title.trim()) return toast.error('Task title is required');
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        title: form.title.trim(),
        description: form.description,
        status: form.status,
        priority: form.priority,
        assigned_to: form.assigned_to || null,
        due_date: form.due_date || null,
        estimated_hours: form.estimated_hours || null,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      await api.post('/tasks', payload);
      toast.success('Task created');
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-title">New Task</div>

        <div className="form-row" style={{ marginBottom: 12 }}>
          <label className="form-label">Task Title *</label>
          <input autoFocus className="form-input" value={form.title} onChange={e => upd('title', e.target.value)}
            placeholder="What needs to be done?" onKeyDown={e => e.key === 'Enter' && handleSave()} />
        </div>

        <div className="form-row" style={{ marginBottom: 12 }}>
          <label className="form-label">Description</label>
          <textarea className="form-input" rows={2} value={form.description}
            onChange={e => upd('description', e.target.value)} placeholder="More details…" style={{ resize: 'vertical' }} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div className="form-row">
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={e => upd('status', e.target.value)}>
              {COLUMNS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Priority</label>
            <select className="form-input" value={form.priority} onChange={e => upd('priority', e.target.value)}>
              {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Assign To</label>
            <select className="form-input" value={form.assigned_to} onChange={e => upd('assigned_to', e.target.value)}>
              <option value="">Unassigned</option>
              {users?.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Due Date</label>
            <input type="date" className="form-input" value={form.due_date} onChange={e => upd('due_date', e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Est. Hours</label>
            <input type="number" className="form-input" value={form.estimated_hours}
              onChange={e => upd('estimated_hours', e.target.value)} placeholder="e.g. 4" />
          </div>
          <div className="form-row">
            <label className="form-label">Tags <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(comma separated)</span></label>
            <input className="form-input" value={form.tags} onChange={e => upd('tags', e.target.value)} placeholder="design, urgent, client" />
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Creating…' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Task Card ─────────────────────────────────────────────────────────────────
function TaskCard({ task, index, onClick, projectColor, onDuplicate }) {
  const pri  = PRIORITY_META[task.priority] || PRIORITY_META.none;
  const days = daysDue(task.due_date);
  const isOver = days !== null && days < 0 && task.status !== 'done';
  const isDueS = days !== null && days <= 1 && days >= 0;
  const subtaskDone = parseInt(task.subtask_done) || 0;
  const subtaskAll  = parseInt(task.subtask_count) || 0;
  const [hovered, setHovered] = useState(false);

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onClick(task)}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            ...provided.draggableProps.style,
            marginBottom: 8,
            background: snapshot.isDragging ? '#fff' : 'var(--surface)',
            borderRadius: 10,
            border: `1px solid ${snapshot.isDragging ? 'var(--accent)' : 'var(--border)'}`,
            padding: '12px 14px',
            boxShadow: snapshot.isDragging ? '0 12px 32px rgba(7,62,129,0.2)' : '0 1px 3px rgba(0,0,0,0.06)',
            cursor: 'grab',
            userSelect: 'none',
          }}
        >
          {/* Priority dot + title + duplicate */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: pri.dot, marginTop: 5, flexShrink: 0 }} />
            <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4, flex: 1,
              textDecoration: task.status === 'done' ? 'line-through' : 'none',
              color: task.status === 'done' ? 'var(--text3)' : 'var(--text1)' }}>
              {task.title}
            </div>
            <button
              onClick={e => { e.stopPropagation(); onDuplicate(task); }}
              title="Duplicate task"
              style={{
                opacity: hovered ? 1 : 0, transition: 'opacity .15s',
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 13, color: 'var(--text3)', padding: '0 2px', lineHeight: 1, flexShrink: 0,
              }}>⧉</button>
          </div>

          {/* Tags */}
          {task.tags?.length > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
              {task.tags.map(tag => (
                <span key={tag} style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10,
                  background: projectColor + '22', color: projectColor, fontWeight: 600 }}>{tag}</span>
              ))}
            </div>
          )}

          {/* Footer */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
            {task.due_date && (
              <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 8,
                background: isOver ? '#FEF2F2' : isDueS ? '#FFFBEB' : 'var(--surface2)',
                color: isOver ? '#DC2626' : isDueS ? '#D97706' : 'var(--text3)' }}>
                📅 {fmtDate(task.due_date)}
              </span>
            )}
            {subtaskAll > 0 && (
              <span style={{ fontSize: 10, color: 'var(--text3)' }}>☑ {subtaskDone}/{subtaskAll}</span>
            )}
            {parseInt(task.comment_count) > 0 && (
              <span style={{ fontSize: 10, color: 'var(--text3)' }}>💬 {task.comment_count}</span>
            )}
            <div style={{ flex: 1 }} />
            {task.assignee_name && (
              <div title={task.assignee_name} style={{ width: 22, height: 22, borderRadius: '50%',
                background: projectColor, color: '#fff', fontSize: 9, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {initials(task.assignee_name)}
              </div>
            )}
          </div>
        </div>
      )}
    </Draggable>
  );
}

// ── Task Detail Panel ─────────────────────────────────────────────────────────
function TaskPanel({ taskId, projectId, users, projectColor, onClose }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [comment, setComment] = useState('');
  const [posting, setPosting]   = useState(false);
  const [newSubtask, setNewSubtask] = useState('');
  const [newSubtaskAssignee, setNewSubtaskAssignee] = useState('');

  // Only team-role users can be subtask assignees
  const designers = (users || []).filter(u => u.role === 'team');

  const { data: task, refetch } = useQuery({
    queryKey: ['task-detail', taskId],
    queryFn: () => api.get(`/tasks/${taskId}`).then(r => r.data),
    staleTime: 0,
  });

  // Invalidate board when any field changes — but keep panel open
  function invalidateBoard() {
    qc.invalidateQueries(['project-tasks', projectId]);
    qc.invalidateQueries(['my-tasks']);
    refetch();
  }

  async function updateField(data) {
    try {
      await api.patch(`/tasks/${taskId}`, data);
      invalidateBoard();
    } catch { toast.error('Failed to save change'); }
  }

  async function postComment() {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/tasks/${taskId}/comments`, { body: comment });
      setComment('');
      refetch();
    } catch { toast.error('Failed to post comment'); }
    finally { setPosting(false); }
  }

  async function deleteComment(cid) {
    await api.delete(`/tasks/${taskId}/comments/${cid}`);
    refetch();
  }

  async function addSubtask() {
    if (!newSubtask.trim()) return;
    await api.post('/tasks', {
      project_id: projectId,
      parent_id: taskId,
      title: newSubtask.trim(),
      status: 'todo',
      assigned_to: newSubtaskAssignee || null,
    });
    setNewSubtask('');
    setNewSubtaskAssignee('');
    refetch();
  }

  async function toggleSubtask(st) {
    await api.patch(`/tasks/${st.id}`, { status: st.status === 'done' ? 'todo' : 'done' });
    refetch();
    invalidateBoard();
  }

  async function duplicateTask() {
    try {
      await api.post('/tasks', {
        project_id: projectId,
        title: task.title + ' (Copy)',
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        assigned_to: task.assigned_to || null,
        due_date: task.due_date ? task.due_date.slice(0, 10) : null,
        estimated_hours: task.estimated_hours || null,
        tags: task.tags || [],
      });
      toast.success('Task duplicated');
      invalidateBoard();
    } catch { toast.error('Failed to duplicate task'); }
  }

  async function deleteTask() {
    if (!window.confirm('Delete this task permanently?')) return;
    await api.delete(`/tasks/${taskId}`);
    invalidateBoard();
    onClose();
  }

  function actIcon(action) {
    const m = { created: '✨', status_changed: '🔄', assigned: '👤', priority_changed: '🎯', comment_added: '💬', due_date_changed: '📅' };
    return m[action] || '•';
  }

  if (!task) return (
    <div style={{ position:'fixed',top:0,right:0,bottom:0,width:'clamp(360px,42vw,560px)',zIndex:300,
      background:'var(--surface)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'-4px 0 32px rgba(0,0,0,0.15)' }}>
      <div className="spinner" />
    </div>
  );

  const pri = PRIORITY_META[task.priority] || PRIORITY_META.medium;

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0,
      width: 'clamp(360px,42vw,560px)', zIndex: 300,
      background: 'var(--surface)',
      boxShadow: '-4px 0 40px rgba(0,0,0,0.18)',
      display: 'flex', flexDirection: 'column',
      animation: 'slideInRight .2s ease',
    }}>
      {/* Header */}
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6, fontWeight: 600 }}>
              {task.project_name}
            </div>
            <div contentEditable suppressContentEditableWarning
              style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.3, outline: 'none', cursor: 'text',
                textDecoration: task.status === 'done' ? 'line-through' : 'none',
                color: task.status === 'done' ? 'var(--text3)' : 'var(--text1)',
                borderBottom: '1px dashed transparent',
              }}
              onFocus={e => e.target.style.borderBottomColor = 'var(--accent)'}
              onBlur={e => {
                e.target.style.borderBottomColor = 'transparent';
                const v = e.target.innerText.trim();
                if (v && v !== task.title) updateField({ title: v });
              }}>
              {task.title}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 20, color: 'var(--text3)', padding: '0 4px', lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>

        {/* Quick controls — these save immediately, panel stays open */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>Status</div>
            <select value={task.status}
              onChange={e => updateField({ status: e.target.value })}
              style={{ fontSize: 11, fontWeight: 700, padding: '5px 8px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--surface2)', cursor: 'pointer' }}>
              {COLUMNS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>Priority</div>
            <select value={task.priority}
              onChange={e => updateField({ priority: e.target.value })}
              style={{ fontSize: 11, fontWeight: 700, padding: '5px 8px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--surface2)', cursor: 'pointer', color: pri.color }}>
              {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 9, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>Assignee</div>
            <select value={task.assigned_to || ''}
              onChange={e => updateField({ assigned_to: e.target.value || null })}
              style={{ fontSize: 11, padding: '5px 8px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--surface2)', cursor: 'pointer' }}>
              <option value="">Unassigned</option>
              {users?.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
        {/* Due + Hours */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>Due Date</div>
            <input type="date" className="form-input" key={task.due_date}
              defaultValue={task.due_date?.slice(0, 10) || ''}
              onBlur={e => updateField({ due_date: e.target.value || null })}
              style={{ fontSize: 12 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 5 }}>Est. Hours</div>
            <input type="number" className="form-input" key={task.estimated_hours}
              defaultValue={task.estimated_hours || ''}
              onBlur={e => updateField({ estimated_hours: e.target.value || null })}
              placeholder="e.g. 4" style={{ fontSize: 12 }} />
          </div>
        </div>

        {/* Description */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Description</div>
          <textarea className="form-input" key={task.description}
            defaultValue={task.description}
            onBlur={e => { if (e.target.value !== task.description) updateField({ description: e.target.value }); }}
            placeholder="Add more context about this task…" rows={3} style={{ fontSize: 13, resize: 'vertical' }} />
        </div>

        {/* Subtasks */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Subtasks {task.subtasks?.length > 0 && `(${task.subtasks.filter(s => s.status === 'done').length}/${task.subtasks.length} done)`}
          </div>
          {(task.subtasks || []).map(st => (
            <div key={st.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
              <input type="checkbox" checked={st.status === 'done'} onChange={() => toggleSubtask(st)}
                style={{ cursor: 'pointer', width: 15, height: 15 }} />
              <span style={{ fontSize: 13, flex: 1,
                textDecoration: st.status === 'done' ? 'line-through' : 'none',
                color: st.status === 'done' ? 'var(--text3)' : 'var(--text1)' }}>{st.title}</span>
              {st.assignee_name && <span style={{ fontSize: 10, color: 'var(--text3)' }}>{st.assignee_name}</span>}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <input className="form-input" placeholder="Add a subtask…" value={newSubtask}
              onChange={e => setNewSubtask(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSubtask()}
              style={{ fontSize: 12, flex: 1, minWidth: 160 }} />
            {designers.length > 0 && (
              <select
                value={newSubtaskAssignee}
                onChange={e => setNewSubtaskAssignee(e.target.value)}
                style={{ fontSize: 12, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text1)', background: 'var(--surface)' }}
              >
                <option value="">Assign to…</option>
                {designers.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name}</option>
                ))}
              </select>
            )}
            <button className="btn-ghost" style={{ fontSize: 12 }} onClick={addSubtask}>Add</button>
          </div>
        </div>

        {/* Comments */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>
            Comments {task.comments?.length > 0 && `(${task.comments.length})`}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14 }}>
            {(task.comments || []).map(c => (
              <div key={c.id} style={{ display: 'flex', gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: projectColor,
                  color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', flexShrink: 0 }}>
                  {initials(c.full_name)}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 700 }}>{c.full_name}</span>
                    <span style={{ fontSize: 10, color: 'var(--text3)' }}>
                      {new Date(c.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {(c.user_id === user?.id || user?.role === 'owner') && (
                      <button onClick={() => deleteComment(c.id)}
                        style={{ fontSize: 10, color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 'auto' }}>✕</button>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{c.body}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <textarea className="form-input" placeholder="Write a comment… (Enter to send)"
              value={comment} onChange={e => setComment(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postComment(); } }}
              rows={2} style={{ flex: 1, fontSize: 13, resize: 'none' }} />
            <button className="btn-primary" onClick={postComment}
              disabled={posting || !comment.trim()} style={{ fontSize: 12, alignSelf: 'flex-end', padding: '8px 14px' }}>
              {posting ? '…' : 'Send'}
            </button>
          </div>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 4 }}>Press Enter to send · Shift+Enter for new line</div>
        </div>

        {/* Activity log */}
        {task.activities?.length > 0 && (
          <div>
            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 10 }}>Activity</div>
            {task.activities.map(a => (
              <div key={a.id} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 12, color: 'var(--text2)' }}>
                <span style={{ flexShrink: 0 }}>{actIcon(a.action)}</span>
                <div>
                  <strong>{a.full_name}</strong> {a.action.replace(/_/g, ' ')}
                  {a.old_value && a.new_value && <> · <em style={{ color: '#DC2626' }}>{a.old_value}</em> → <em style={{ color: '#059669' }}>{a.new_value}</em></>}
                  <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 8 }}>
                    {new Date(a.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 24px', borderTop: '1px solid var(--border)', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 11, color: 'var(--text3)' }}>
          Created {task.created_at ? new Date(task.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : ''}
          {task.created_by_name ? ` by ${task.created_by_name}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={duplicateTask} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}>
            ⧉ Duplicate
          </button>
          <button onClick={deleteTask} style={{ fontSize: 11, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer' }}>
            🗑 Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Board Page ────────────────────────────────────────────────────────────────
export default function ProjectBoardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const { user } = useAuth();

  const [activeTaskId, setActiveTaskId] = useState(null);
  const [view, setView] = useState('board');
  const [newTaskStatus, setNewTaskStatus] = useState('todo');
  const [showNewTask, setShowNewTask] = useState(false);

  // Deep link from a notification (?task=<id>) — open that task's detail on arrival
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (taskId) {
      setActiveTaskId(taskId);
      const next = new URLSearchParams(searchParams);
      next.delete('task');
      setSearchParams(next, { replace: true });
    }
  }, []);

  const { data: project } = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.get(`/projects/${id}`).then(r => r.data),
  });

  const { data: tasksRaw = [], refetch: refetchTasks } = useQuery({
    queryKey: ['project-tasks', id],
    queryFn: () => api.get(`/projects/${id}/tasks`).then(r => r.data),
    refetchInterval: 30000, // Auto-refresh every 30s — picks up team members' changes
  });

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then(r => r.data),
  });

  const columns = COLUMNS.reduce((acc, col) => {
    acc[col.id] = tasksRaw.filter(t => t.status === col.id).sort((a, b) => a.order_index - b.order_index);
    return acc;
  }, {});

  async function duplicateTask(task) {
    try {
      await api.post('/tasks', {
        project_id: id,
        title: task.title + ' (Copy)',
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        assigned_to: task.assigned_to || null,
        due_date: task.due_date ? task.due_date.slice(0, 10) : null,
        estimated_hours: task.estimated_hours || null,
        tags: task.tags || [],
      });
      toast.success('Task duplicated');
      refetchTasks();
    } catch { toast.error('Failed to duplicate task'); }
  }

  async function onDragEnd(result) {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const newStatus = destination.droppableId;

    // Optimistic update
    qc.setQueryData(['project-tasks', id], old => old?.map(t =>
      t.id === draggableId ? { ...t, status: newStatus, order_index: destination.index } : t
    ));

    try {
      await api.patch(`/tasks/${draggableId}`, { status: newStatus, order_index: destination.index });
      refetchTasks();
    } catch {
      toast.error('Failed to move task');
      refetchTasks();
    }
  }

  if (!project) return <div className="loading-screen"><div className="spinner" /></div>;

  const totalTasks = tasksRaw.length;
  const doneTasks  = tasksRaw.filter(t => t.status === 'done').length;
  const pct = totalTasks > 0 ? Math.round(doneTasks / totalTasks * 100) : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
      {/* Project header */}
      <div style={{ flexShrink: 0, paddingBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/projects')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 13, padding: 0 }}>
            ← Projects
          </button>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: project.color }} />
          <div style={{ fontSize: 18, fontWeight: 800 }}>{project.name}</div>
          {project.client_name && (
            <span style={{ fontSize: 12, color: 'var(--text2)', background: 'var(--surface2)', padding: '2px 10px', borderRadius: 10 }}>
              {project.client_name}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {/* New task button */}
          <button className="btn-primary" style={{ fontSize: 12 }}
            onClick={() => { setNewTaskStatus('todo'); setShowNewTask(true); }}>
            + New Task
          </button>
          {/* View toggle */}
          <div style={{ display: 'flex', gap: 4 }}>
            {[['board', '⊞ Board'], ['list', '≡ List']].map(([v, label]) => (
              <button key={v} onClick={() => setView(v)} style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 12, border: '1px solid var(--border)',
                background: view === v ? 'var(--accent)' : 'var(--surface)',
                color: view === v ? '#fff' : 'var(--text2)', cursor: 'pointer', fontWeight: view === v ? 700 : 400,
              }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Progress + members */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: project.color, borderRadius: 3, transition: 'width .4s' }} />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{doneTasks}/{totalTasks} done · {pct}%</span>
          <div style={{ display: 'flex' }}>
            {(project.members || []).slice(0, 5).map((m, i) => (
              <div key={m.user_id} title={m.full_name} style={{
                width: 26, height: 26, borderRadius: '50%', background: project.color, color: '#fff',
                fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginLeft: i > 0 ? -8 : 0, border: '2px solid var(--surface)', zIndex: 5 - i,
              }}>{initials(m.full_name)}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ── BOARD VIEW ── */}
      {view === 'board' && (
        <DragDropContext onDragEnd={onDragEnd}>
          <div style={{ display: 'flex', gap: 12, flex: 1, overflowX: 'auto', paddingBottom: 16, alignItems: 'flex-start' }}>
            {COLUMNS.map(col => {
              const colTasks = columns[col.id] || [];
              return (
                <div key={col.id} style={{
                  width: 272, minWidth: 272, display: 'flex', flexDirection: 'column',
                  background: col.bg, borderRadius: 12, border: '1px solid var(--border)',
                  maxHeight: '100%',
                }}>
                  {/* Column header */}
                  <div style={{ padding: '12px 14px 8px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: col.color }} />
                    <div style={{ fontWeight: 700, fontSize: 13, color: col.color }}>{col.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', background: 'rgba(0,0,0,0.06)',
                      padding: '1px 8px', borderRadius: 10, marginLeft: 'auto' }}>
                      {colTasks.length}
                    </div>
                    <button title={`Add task to ${col.label}`}
                      onClick={() => { setNewTaskStatus(col.id); setShowNewTask(true); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: col.color, fontSize: 16, padding: '0 2px', lineHeight: 1 }}>
                      +
                    </button>
                  </div>

                  {/* Droppable */}
                  <Droppable droppableId={col.id}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                          padding: '0 10px 10px',
                          flex: 1,
                          overflowY: 'auto',
                          minHeight: 120,
                          background: snapshot.isDraggingOver ? `${col.color}18` : 'transparent',
                          transition: 'background .15s',
                          borderRadius: '0 0 12px 12px',
                        }}
                      >
                        {colTasks.map((task, i) => (
                          <TaskCard key={task.id} task={task} index={i}
                            projectColor={project.color}
                            onClick={t => setActiveTaskId(t.id)}
                            onDuplicate={duplicateTask} />
                        ))}
                        {provided.placeholder}
                        {colTasks.length === 0 && !snapshot.isDraggingOver && (
                          <div style={{ textAlign: 'center', padding: '20px 0', fontSize: 12, color: 'var(--text3)' }}>
                            Drop tasks here
                          </div>
                        )}
                      </div>
                    )}
                  </Droppable>
                </div>
              );
            })}
          </div>
        </DragDropContext>
      )}

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {COLUMNS.map(col => {
            const colTasks = columns[col.id] || [];
            if (colTasks.length === 0) return null;
            return (
              <div key={col.id} style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
                  padding: '7px 14px', background: col.bg, borderRadius: 8, borderLeft: `3px solid ${col.color}` }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: col.color }}>{col.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{colTasks.length}</span>
                </div>
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
                  {colTasks.map(task => {
                    const pri = PRIORITY_META[task.priority] || PRIORITY_META.none;
                    const days = daysDue(task.due_date);
                    return (
                      <div key={task.id} onClick={() => setActiveTaskId(task.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
                          borderBottom: '1px solid var(--border)', cursor: 'pointer', fontSize: 13 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: pri.dot, flexShrink: 0 }} />
                        <div style={{ flex: 1, fontWeight: 600,
                          textDecoration: task.status === 'done' ? 'line-through' : 'none',
                          color: task.status === 'done' ? 'var(--text3)' : 'var(--text1)' }}>
                          {task.title}
                        </div>
                        <span style={{ fontSize: 11, color: pri.color, fontWeight: 700 }}>{task.priority !== 'none' ? pri.label : ''}</span>
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>{task.assignee_name || '—'}</span>
                        <span style={{ fontSize: 11, color: days !== null && days < 0 ? '#DC2626' : 'var(--text3)', fontWeight: days !== null && days < 0 ? 700 : 400 }}>
                          {task.due_date ? fmtDate(task.due_date) : '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Task Detail Panel */}
      {activeTaskId && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 299, background: 'rgba(0,0,0,0.2)' }}
            onClick={() => setActiveTaskId(null)} />
          <TaskPanel
            taskId={activeTaskId}
            projectId={id}
            users={users}
            projectColor={project.color}
            onClose={() => setActiveTaskId(null)}
          />
        </>
      )}

      {/* New Task Modal */}
      {showNewTask && (
        <NewTaskModal
          projectId={id}
          defaultStatus={newTaskStatus}
          users={users}
          onClose={() => setShowNewTask(false)}
          onSaved={() => { refetchTasks(); qc.invalidateQueries(['my-tasks']); }}
        />
      )}
    </div>
  );
}
